<template>
  <div>
    <Form
      ref="bookingForm"
      class="booking-page"
      :class="{ desktop: $isDesktop }"
      data-test="booking-form"
      @submit="submitForm()"
      @invalid-submit="scrollToFirstError"
    >
      <div
        v-observe-visibility="{
          callback: sectionHeaderVisibilityChanged,
          once: true,
          throttle: 300,
          intersection: {
            threshold: 0.1,
          },
          throttleOptions: {
            leading: 'visible',
          },
        }"
        class="section-header"
        data-booking-progress="1"
      >
        <translate>My details</translate>
      </div>
      <section v-if="contactDetails" class="my-details-wrapper">
        <booking-my-details
          :key="authStore.isLoggedIn"
          :campsite-name="details.campsiteName"
          :contact-details="contactDetails"
          :logged-in="authStore.isLoggedIn"
          :logged-in-user-email="authStore.userEmail"
          :state-to-strings="stateToStrings"
          @update:model-value="bookingMyDetailsInput($event)"
          @logout="handleLogout"
          @email-blur="registerInterest"
        />
      </section>

      <div
        v-observe-visibility="{
          callback: sectionHeaderVisibilityChanged,
          once: true,
          throttle: 300,
          throttleOptions: {
            leading: 'visible',
          },
        }"
        class="section-header"
        data-booking-progress="4"
      >
        <translate>Other details</translate>
      </div>
      <section>
        <booking-other-details
          ref="bookingOtherDetails"
          :adults="params.party.adults"
          :campsite-name="details.campsiteName"
          :child-ages="params.party.childAges"
          :details="details"
          :dimensions="details.dimensions"
          :dimensions-example="details.pitchTypeDimensionsExample"
          :enforce-special-requests="details.enforceSpecialRequests"
          :is-byo="details.isByo"
          :other-details="otherDetails"
          :require-car-registration="details.requireCarRegistration"
          :require-party-names="details.requirePartyNames"
          :request-group-name="details.requestGroupName"
          @update:model-value="updateOtherDetails($event)"
        />
      </section>

      <div class="section-header">
        <translate>Total cost</translate>
      </div>
      <section class="total-cost">
        <booking-costs
          :selected-extras="selectedExtras"
          :quote="quote"
          @update:selected-extras="selectedExtraChanged($event)"
        />

        <booking-total-cost
          v-if="paymentMethodDetails"
          v-model:take-payment-immediately="takePaymentImmediately"
          :details="details"
          :quote="quote"
          @take-payment-immediately-changed="payAllNow = $event"
        />

        <booking-tax-terms :details="details" />

        <div v-if="details.usefulInfo" v-html="details.usefulInfo" />
        <div
          v-for="(usefulinfo, index) of details.termsUsefulInfo"
          :key="index"
        >
          <div class="usefullinfo" v-html="usefulinfo" />
        </div>
        <translate
          v-if="isTotalPriceCurrencyDifferentThanUsersCurrency"
          tag="p"
          class="currency-footnote"
        >
          Amounts in brackets are indicative based on today's exchange rate -
          the amount paid will depend on your payment provider's exchange rate.
        </translate>
      </section>

      <div class="section-header cancellation-policy">
        <translate tag="span" :tt="{ campsiteName: details.campsiteName }">
          {campsiteName} cancellation policy
        </translate>
      </div>
      <booking-cancellation-policy
        :campsite-name="details.campsiteName"
        :cancellation-policy="details.cancellationPolicy"
        :needs-translation="details.cancellationPolicyNeedsTranslation"
      />

      <template
        v-if="
          details.notices ||
          details.campsiteNotices.length ||
          details.arrivalFrom ||
          details.requireEstimatedTimeOfArrival ||
          otherDetails.estimatedTimeOfArrival
        "
      >
        <div class="section-header">
          <span>
            <img
              class="icon"
              src="@/assets/images/exclamation-mark-white.svg"
              width="32"
              height="32"
              alt=""
            />
            <translate>Please note</translate>
          </span>
        </div>
        <section class="please-note">
          <permitted-arrival-departure-times-section
            class="permitted-times"
            :arrival-from="details.arrivalFrom"
            :arrival-to="details.arrivalTo"
            :depart-from="details.departFrom"
            :arrival-time="details.arrivalTime"
          />

          <booking-estimate-time-of-arrival
            v-if="details.requireEstimatedTimeOfArrival"
            v-model:estimated-time-of-arrival="
              otherDetails.estimatedTimeOfArrival
            "
            :estimated-time-of-arrival-compulsory="
              details.isEstimatedTimeOfArrivalCompulsory
            "
            :campsite-name="details.campsiteName"
            :arrival-from="details.arrivalFromObj"
            :arrival-to="details.arrivalToObj"
          />

          <campsite-notices
            :warnings="details.warnings"
            :notices="details.notices"
            :notices-needs-translation="details.noticesNeedsTranslation"
            :campsite-notices="details.campsiteNotices"
            class="campsite-notices"
          />
        </section>
      </template>

      <div
        v-observe-visibility="{
          callback: sectionHeaderVisibilityChanged,
          once: true,
          throttle: 300,
          throttleOptions: {
            leading: 'visible',
          },
        }"
        class="section-header secure-section"
        data-booking-progress="6"
      >
        <span>
          <img
            src="@/assets/images/padlock.svg"
            class="padlock"
            width="54"
            height="54"
            alt=""
          />
          <translate>Secure payment</translate>
          <pu-popover>
            <template #trigger>
              <div class="secure-note-popover-trigger">
                <translate>We are safe and secure</translate>.
                <img
                  src="@/assets/images/info.svg"
                  width="24"
                  height="24"
                  alt=""
                />
              </div>
            </template>
            <template #content>
              <div class="secure-note-popover-contents">
                <translate tag="p"
                  >We use a secure server to capture your card details from the
                  booking form, using the industry-standard 'Secure Socket
                  Layer' (SSL) technology to encrypt all your personal and card
                  details. You can verify that the page is securing by checking
                  that there's an https:// prefix in the web address of the
                  page, and a key or padlock icon in the browser's status
                  bar.</translate
                >
              </div>
            </template>
          </pu-popover>
        </span>
      </div>

      <section class="checkout">
        <ui-collapse :collapse-id="`collapse-terms-and-conditions`">
          <template #ui-collapse-title>
            <translate tag="div" class="ts-and-cs-title">
              Terms and conditions
            </translate>
          </template>
          <template #ui-collapse-body>
            <div
              ref="campsiteTermsAndConditions"
              v-thtml="details.campsiteTermsAndConditions"
              class="ts-and-cs-content"
            />
            <lazy-google-translate-button
              v-if="details.termsAndConditionsNeedsTranslation"
              :parent="campsiteTermsAndConditionsRef"
            />
          </template>
        </ui-collapse>

        <translate tag="p" class="accept-terms">
          By making this booking I agree to accept the terms and conditions.
        </translate>

        <div v-if="details.warnings.length">
          <ul>
            <li
              v-for="(warning, index) in details.warnings"
              :key="index"
              class="guests-age-warning"
            >
              <img
                src="@/assets/images/exclamation-mark.svg"
                width="18"
                height="18"
                loading="lazy"
              />{{ warning }}
            </li>
          </ul>
        </div>

        <div v-if="details.ageWarning" class="guests-age-warning">
          <img
            src="@/assets/images/exclamation-mark.svg"
            width="18"
            height="18"
            loading="lazy"
          />
          <p>{{ details.ageWarning }}</p>
        </div>

        <div class="submit-wrapper">
          <client-only>
            <booking-paypal-button
              v-if="quote.paypalEnabled"
              :booking-id="context.bookingId"
              :currency="quote.deposit.currency"
              :payment-id="context.taskId"
              :locale="paypalLocale"
              :should-close="paypalShouldClose"
              class="paypal-button"
              @cancelled="paypalCancelled"
              @click="paypalClicked"
              @closed="paypalClosed"
              @error="paypalError"
              @paid="paypalPaid"
            />
          </client-only>

          <div class="pay-with-card">
            <client-only>
              <lazy-payments-methods-form-2
                ref="paymentsMethodsForm2"
                :is-immediate-payment="takePaymentImmediately"
                :is-pitchup-only-payment="isPitchupOnlyPayment"
                :currency="quote.deposit.currency"
                :amount="
                  takePaymentImmediately
                    ? quote.priceTotal.amountMinor
                    : quote.deposit.amountMinor
                "
                :details="paymentMethodDetails"
                :validation-errors="paymentValidationErrors"
                :price="
                  takePaymentImmediately ? quote.priceTotal : quote.deposit
                "
                :label="details.pitchTypeName"
                :show="{
                  saveNewCard: authStore.isLoggedIn,
                  saveNewCardLoginInfo: !authStore.isLoggedIn,
                }"
                :email="contactDetails.email"
                @input="paymentMethodUpdated($event)"
                @stripe-init="stripeInitialized = true"
                @payment-element-error="handlePaymentElementError"
              />
            </client-only>

            <base-submit-button
              data-test="booking-submit"
              :disabled="stateToStrings.includes('submitting')"
              :submitting="stateToStrings.includes('submitting')"
              class="primary asblock book-now"
            >
              <template #default>
                <translate>Book now</translate>
              </template>
              <template #submittingText>
                <translate>Processing…</translate>
              </template>
            </base-submit-button>
          </div>

          <translate
            tag="p"
            class="help-text"
            :tt="{ email: contactDetails.email }"
          >
            You will receive a confirmation email to {email}.
          </translate>
        </div>
      </section>
    </Form>
  </div>
</template>

<script lang="ts">
import { useNuxtApp } from 'nuxt/app'
import { v4 as uuidv4 } from 'uuid'
import { Form } from 'vee-validate'
import { getLocaleInfo } from '~/lang/locales'
import { logger } from '~/utility/logger'
import AuthMixin from '~/mixins/AuthMixin'
import VueHooksInfoMixin from '~/mixins/VueHooksInfoMixin'
import { useStripeStore } from '~/stores/useStripeStore'
import { useCountriesStore } from '~/stores/useCountriesStore'
import {
  Component,
  Prop,
  Setup,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { PaymentMethodDetails } from '../../payments/types'
import type { BookingInfo } from '../data/schema'
import type { BookingContext, BookingStateEvent } from '../machines/types'
import type { Quote, QuoteRequestParams } from '../quote/schema'
import type { SelectedExtras } from '../schema'
import type {
  ContactDetails,
  OtherDetails,
  StayDetails,
} from '../submit/schema'
import BookingOtherDetails from './BookingOtherDetails.vue'

@Component({
  mixins: [AuthMixin, VueHooksInfoMixin],
  components: { Form },
  setup: () => {
    const { $log } = useNuxtApp()

    return {
      authStore: useAuthStore(usePinia()),
      countriesStore: useCountriesStore(usePinia()),
      currencyStore: useCurrencyStore(usePinia()),
      stripeStore: useStripeStore(usePinia()),
      log: $log,
    }
  },
})
class BookingForm extends Vue {
  authStore: ReturnType<typeof useAuthStore>
  countriesStore: ReturnType<typeof useCountriesStore>
  currencyStore: ReturnType<typeof useCurrencyStore>
  stripeStore: ReturnType<typeof useStripeStore>
  log: (event: string, data?: Record<string, any>) => void

  declare $refs: {
    campsiteTermsAndConditions: Element
    bookingOtherDetails: typeof BookingOtherDetails
    bookingForm: InstanceType<typeof Form>
    paymentsMethodsForm2: any
  }

  @Prop()
  userContactDetails: ContactDetails

  @Prop()
  userOtherDetails: OtherDetails

  @Prop()
  params!: QuoteRequestParams

  @Prop()
  context: BookingContext

  @Prop()
  send: (event: BookingStateEvent) => void

  @Prop()
  submitErrors: Record<string, string[]>

  @Prop()
  details: BookingInfo

  @Prop()
  stateToStrings: string[]

  @Prop()
  quote: Quote

  // paymentRequestButtonKey = 0
  stripeInitialized = false

  async mounted() {
    try {
      if (useRouter().currentRoute?.value?.hash) {
        await useRouter().push({
          hash: '',
          query: useRouter().currentRoute.value.query,
        })
      }
    } catch (e) {
      console.error(e)
    }

    const el = this.$el
    const inputs = el.querySelectorAll('input, textarea')
    inputs.forEach((currentEl) => {
      currentEl.addEventListener('blur', () => {
        try {
          if (
            this.$refs.bookingForm &&
            Object.keys(this.$refs.bookingForm.errors).length
          ) {
            Object.keys(this.$refs.bookingForm.errors).forEach((key) => {
              const gtmEvent = {
                event: 'booking_form_error',
                field: key,
                message: this.$refs.bookingForm.errors[key],
                trigger: 'blur',
              }
              this.$gtm.push(gtmEvent)
            })
          }
        } catch (e) {
          console.error(e)
        }
      })
    })

    if (!this.$refs.bookingForm) {
      console.error('BookingForm: bookingForm ref not available in mounted()')
    }
    this.formValues = this.$refs.bookingForm.values
  }

  formValues: Record<string, any> = {}
  formIsValid = false

  @Watch('formValues', { deep: true })
  async formValuesChanged() {
    const result = await this.$refs.bookingForm?.validate({ mode: 'silent' })
    this.formIsValid = result.valid
  }

  // @Watch('takePaymentImmediately', { immediate: true })
  // takePaymentImmediatelyChanged() {
  //   this.paymentRequestButtonKey++
  //   this.stripeInitialized = false
  //   this.log('stripe_payment_mode_changed', {
  //     takePaymentImmediately: this.takePaymentImmediately,
  //     requestButtonKey: this.paymentRequestButtonKey,
  //   })
  // }

  @Watch('stripeStore.stripeToken')
  onStripeTokenChanged() {
    this.log('stripe_token_changed', {
      hasToken: !!this.stripeStore.stripeToken,
      customerId: this.stripeStore.stripeToken?.customer_id || 'none',
      method: this.paymentMethodDetails?.method,
    })
  }

  payAllNow = false

  paymentValidationErrors: string[] = []

  handleLogout() {
    this.authStore.logOutInPlace()
    useFormStore().clearFormData()
    this.send({ type: 'UPDATE_CONTACT_DETAILS', details: {} as ContactDetails })
    this.send({ type: 'EMAIL_CHANGED', email: '' })
  }

  @Setup((props) => {
    return {
      chargeTypeId: '',
      pitchtypeId: props.params.pitchtypeId,
      party: {
        adults: props.params.party.adults,
        childAges: props.params.party.childAges,
      },
      range: props.params.stayRange,
    }
  })
  stayDetails: StayDetails

  paymentMethodDetails: PaymentMethodDetails = {
    method: 'not-selected',
  }

  @Setup(() => {
    const route = useRoute()
    return getLocaleInfo((route.params.lang as string) || 'en-gb').paypal
  })
  paypalLocale: ReturnType<typeof getLocaleInfo>

  paypalShouldClose = false

  get campsiteTermsAndConditionsRef() {
    // refs are not reactive, so need this little trick to make sure it's there when needed
    if (!this.isMounted) {
      return undefined
    }
    return this.$refs.campsiteTermsAndConditions
  }

  get takePaymentImmediately() {
    return this.quote.paymentWhen === 'immediate_payment' || this.payAllNow
  }

  get isPitchupOnlyPayment() {
    return this.quote.paymentWhen === 'pitchup_only'
  }

  async paypalClicked() {
    // First ensure we're not in a submitting state
    if (this.stateToStrings.includes('submitting')) {
      return
    }

    this.paypalShouldClose = false

    // Set payment method and ensure it's updated in the component state
    this.paymentMethodDetails = { method: 'paypal' }
    this.clearPaymentErrors()

    // Validate form first before submitting
    const valid = await this.bookingFormIsValid()
    if (!valid) {
      this.paypalShouldClose = true
      this.scrollToFirstError()
      return
    }

    // Double check payment method is still paypal
    if (this.paymentMethodDetails.method !== 'paypal') {
      this.paymentMethodDetails = { method: 'paypal' }
    }

    // Validate payment method
    const paymentValid = await this.validatePaymentMethod()
    if (!paymentValid) {
      this.paypalShouldClose = true
      this.scrollToFirstError()
      return
    }

    // Send the submit event - this will trigger the state machine to generate paymentId and bookingId
    this.sendBookingMachineSubmit()
  }

  paypalClosed() {
    this.paymentMethodUpdated({ method: 'not-selected' })
    this.paypalShouldClose = false
  }

  paypalCancelled() {
    this.paypalClosed()
    this.send({ type: 'PAYPAL_CANCELLED' })
  }

  paypalError() {
    this.paypalClosed()
    this.send({ type: 'PAYPAL_ERROR' })
  }

  async paypalPaid(path: string) {
    this.send({ type: 'PAYPAL_PAID' })
    await this.$router.push({ path })
  }

  registerInterest() {
    this.send({ type: 'REGISTER_INTEREST' })
  }

  selectedExtraChanged(event) {
    this.send({ selected: [event], type: 'EXTRAS_CHANGED' })
  }

  @Prop()
  selectedExtras: SelectedExtras

  bookingMyDetailsInput(contactDetails: ContactDetails) {
    this.send({
      type: 'UPDATE_CONTACT_DETAILS',
      details: contactDetails,
    })
    if (contactDetails?.email !== this?.context?.contactDetails?.email) {
      this.send({ type: 'EMAIL_CHANGED', email: contactDetails.email })
    }
  }

  get failedValidationFields() {
    const errors = Object.entries(this.$refs.bookingForm.errors)
    return errors.filter(([_, value]) => value.length).map(([key]) => key)
  }

  get paymentRequestButtonPrice() {
    return this.takePaymentImmediately
      ? this.quote.priceTotal
      : this.quote.deposit
  }

  async bookingFormIsValid() {
    const result = await this.$refs.bookingForm.validate()
    // Return the boolean valid property instead of the full result object
    // VeeValidate's validate() returns { valid: boolean, errors: object }
    return result.valid
  }

  getBookingMeta(): string {
    const win = window as Window & {
      __GA4_CLIENT_ID__?: string
      __GA4_SESSION_ID__?: string
    }

    const ga4Data: Record<string, string> = {}

    if (win.__GA4_CLIENT_ID__) {
      ga4Data.client_id = win.__GA4_CLIENT_ID__
    }
    if (win.__GA4_SESSION_ID__) {
      ga4Data.session_id = win.__GA4_SESSION_ID__
    }

    const bookingMeta = {
      ga4: ga4Data,
    }

    return JSON.stringify(bookingMeta)
  }

  sendBookingMachineSubmit() {
    const stayDetails: StayDetails = {
      chargeTypeId: this.quote.chargetypeId,
      pitchtypeId: this.params.pitchtypeId,
      party: {
        adults: this.params.party.adults,
        childAges: this.params.party.childAges,
        dogs: this.params.party.dogs,
      },
      range: this.params.stayRange,
    }

    // Log the payment submission with Stripe account details
    const stripeAccount = this.stripeStore.stripeToken?.customer_id || null
    this.log('booking_submit_with_stripe', {
      paymentMethod: this.paymentMethodDetails.method,
      takePaymentImmediately: this.takePaymentImmediately,
      hasStripeAccount: !!stripeAccount,
      stripeAccount: stripeAccount || 'none',
    })

    this.send({
      type: 'SUBMIT',
      payload: {
        langCode: this.$route.params.lang || 'en-gb',
        slug: this.params.slug,
        paymentDetails: {
          ...this.paymentMethodDetails,
          takePaymentImmediately: this.takePaymentImmediately,
          stripeAccount: stripeAccount,
        },
        stayDetails,
        trackingDetails: {
          token: uuidv4(),
        },
        bookingMeta: this.getBookingMeta(),
      },
    })
  }

  async submitForm() {
    const valid = await this.bookingFormIsValid()
    const paymentValid = await this.validatePaymentMethod()

    if (
      !valid ||
      !paymentValid ||
      this.paymentValidationErrors.length ||
      this.paymentMethodDetails.error
    ) {
      this.paypalShouldClose = true
      this.scrollToFirstError()
      return
    }

    // Check if payment method requires client-side payment method creation via Stripe
    // This applies to both immediate and deposit flows
    // Saved cards (method: 'card') are excluded - they already have a payment method ID
    const needsPaymentMethodCreation = [
      'new-card',
      'apple-pay',
      'google-pay',
      'idealBank',
    ].includes(this.paymentMethodDetails.method)

    this.log('stripe_validation_check', {
      needsPaymentMethodCreation,
      takePaymentImmediately: this.takePaymentImmediately,
      paymentMethod: this.paymentMethodDetails.method,
      stripeInitialized: this.stripeInitialized,
      hasStripeToken: !!this.stripeStore.stripeToken,
      stripeTokenId: this.stripeStore.stripeToken?.customer_id || 'none',
    })

    // If Stripe payment method creation is needed but not initialized, show error and return
    if (needsPaymentMethodCreation && !this.stripeInitialized) {
      this.log('stripe_initialization_error', {
        paymentMethod: this.paymentMethodDetails.method,
        takePaymentImmediately: this.takePaymentImmediately,
      })
      this.paymentValidationErrors = [
        this.$t(
          'Payment system is still initializing. Please try again in a moment.',
        ),
      ]
      this.scrollToFirstError()
      return
    }

    // Ensure we have a Stripe token when payment method creation is needed
    if (needsPaymentMethodCreation && !this.stripeStore.stripeToken) {
      this.log('stripe_token_missing', {
        paymentMethod: this.paymentMethodDetails.method,
        takePaymentImmediately: this.takePaymentImmediately,
        stripeInitialized: this.stripeInitialized,
      })
      this.paymentValidationErrors = [
        this.$t(
          'Unable to access payment system. Please refresh the page and try again.',
        ),
      ]
      this.scrollToFirstError()
      return
    }

    // For new card and wallet payments, submit stripe element and create payment method
    logger(
      'BookingForm: submitForm - needsPaymentMethodCreation:',
      needsPaymentMethodCreation,
      'method:',
      this.paymentMethodDetails.method,
    )

    if (needsPaymentMethodCreation) {
      this.log('stripe_element_submit_start', {
        paymentMethod: this.paymentMethodDetails.method,
      })

      logger('BookingForm: Getting payments form reference...')
      // Get reference to PaymentsMethodsForm2 which exposes submitAndCreatePaymentMethod
      const paymentsForm = this.$refs.paymentsMethodsForm2
      logger(
        'BookingForm: paymentsForm:',
        !!paymentsForm,
        'hasMethod:',
        !!paymentsForm?.submitAndCreatePaymentMethod,
      )

      if (!paymentsForm?.submitAndCreatePaymentMethod) {
        this.log('stripe_element_not_available', {
          paymentMethod: this.paymentMethodDetails.method,
          hasPaymentsForm: !!paymentsForm,
        })
        this.paymentValidationErrors = [
          this.$t('Payment system not ready. Please try again.'),
        ]
        this.scrollToFirstError()
        return
      }

      // Build billing details from contact details with safe access
      const cd = this.contactDetails

      // Look up the actual dialing code from the country code
      // cd.phone.countryDialingCode contains the country alpha2 code (e.g., "GB")
      // We need to find the actual dialing code (e.g., "44") from the countries store
      let phoneNumber = null
      if (cd?.phone?.countryDialingCode && cd?.phone?.number) {
        const country = this.countriesStore.countries.find(
          (c) => c.alpha2 === cd.phone.countryDialingCode,
        )
        if (country) {
          phoneNumber = `+${country.dialingCode}${cd.phone.number}`
        }
      }

      const billingDetails = {
        name:
          cd?.firstName && cd?.lastName
            ? `${cd.firstName} ${cd.lastName}`
            : null,
        email: cd?.email || null,
        phone: phoneNumber,
        address: {
          line1: cd?.address?.street || null,
          city: cd?.address?.city || null,
          postal_code: cd?.address?.postCode || null,
          country: cd?.address?.country || null,
        },
      }

      // Submit stripe element and create payment method with billing details
      logger(
        'BookingForm: Calling submitAndCreatePaymentMethod() with billing details...',
      )
      const result =
        await paymentsForm.submitAndCreatePaymentMethod(billingDetails)
      logger('BookingForm: submitAndCreatePaymentMethod() result:', result)

      if (!result.success) {
        logger('BookingForm: Payment method creation failed:', result.error)
        this.log('stripe_element_submit_error', {
          paymentMethod: this.paymentMethodDetails.method,
          error: result.error,
        })
        this.paymentValidationErrors = [result.error]
        this.scrollToFirstError()
        return
      }

      logger(
        'BookingForm: Payment method created successfully:',
        result.paymentMethodId,
        'type:',
        result.type,
      )
      this.log('stripe_element_submit_success', {
        paymentMethod: this.paymentMethodDetails.method,
        paymentMethodId: result.paymentMethodId,
        stripePaymentType: result.type,
      })

      // Map Stripe payment method types to our internal types
      const methodTypeMap: Record<string, string> = {
        card: 'new-card',
        ideal: 'idealBank',
        google_pay: 'google-pay',
        apple_pay: 'apple-pay',
      }

      const internalMethodType = methodTypeMap[result.type] || 'new-card'

      // Update payment method details with the correct type and ID
      if (internalMethodType === 'idealBank') {
        // Type assertion needed because idealBank is an inline type in the union
        // Bank selection is handled by Stripe Payment Element during auth flow
        this.paymentMethodDetails = {
          method: 'idealBank',
          bank: '', // Bank is selected during authorization, embedded in PaymentMethod
          paymentMethodId: result.paymentMethodId,
        } as unknown as PaymentMethodDetails
      } else if (internalMethodType === 'google-pay') {
        this.paymentMethodDetails = {
          method: 'google-pay',
          paymentMethodId: result.paymentMethodId,
        }
      } else if (internalMethodType === 'apple-pay') {
        this.paymentMethodDetails = {
          method: 'apple-pay',
          paymentMethodId: result.paymentMethodId,
        }
      } else {
        // For cards and other types
        this.paymentMethodDetails = {
          method: 'new-card',
          saveCard:
            this.paymentMethodDetails.method === 'new-card'
              ? this.paymentMethodDetails.saveCard
              : false,
          paymentMethodId: result.paymentMethodId,
        }
      }
    }

    this.sendBookingMachineSubmit()
  }

  resolveCardId: (value: boolean) => void

  cardIdRetrieved = new Promise<boolean>((resolve) => {
    this.resolveCardId = resolve
  })

  paymentMethodUpdated(event: PaymentMethodDetails) {
    this.paymentMethodDetails = event
    this.clearPaymentErrors()

    if (this.paymentMethodDetails.method === 'new-card') {
      this.resolveCardId(true)
    }
  }

  async validatePaymentMethod() {
    if (this.paymentMethodDetails.method === 'paypal') {
      return true
    }

    if (
      this.paymentMethodDetails.method === 'card' &&
      this.paymentMethodDetails.cardId
    ) {
      return true
    }

    if (this.paymentMethodDetails.method === 'new-card') {
      await this.cardIdRetrieved
    }

    this.clearPaymentErrors()

    if (this.paymentMethodDetails.method === 'not-selected') {
      this.paymentValidationErrors = [this.$t('Please select a payment method')]
      return false
    }

    // For new cards and wallets, we validate and create the payment method during submit
    // (in submitAndCreatePaymentMethod), not here. We just need to verify a method is selected.
    // For saved cards, verify they have a cardId
    if (
      this.paymentMethodDetails.method === 'card' &&
      !this.paymentMethodDetails.cardId
    ) {
      this.paymentValidationErrors = [this.$t('Please select a payment method')]
      return false
    }

    return true
  }

  @Watch('submitErrors', { deep: true })
  updateFormErrors() {
    this.paypalShouldClose = true
    this.$refs.bookingForm.setErrors(this.submitErrors)
    setTimeout(() => {
      if ('text_message' in this.submitErrors) {
        this.$puScrollTo('input[name="telephone"]', {
          duration: 300,
          offset: -50,
        })

        if (Object.keys(this.submitErrors).length) {
          Object.keys(this.submitErrors).forEach((key) => {
            const gtmEvent = {
              event: 'booking_form_error',
              field: key,
              message: Array.isArray(this.submitErrors[key])
                ? this.submitErrors[key].toString()
                : this.submitErrors[key],
              trigger: 'submit',
            }
            this.$gtm.push(gtmEvent)
          })
        }
      } else {
        this.scrollToFirstError()
      }
    }, 500)
  }

  scrollToFirstError() {
    try {
      const form = this.$refs['bookingForm']
      const errors = form.errors

      if (Object.keys(errors).length) {
        Object.keys(errors).forEach((key) => {
          const gtmEvent = {
            event: 'booking_form_error',
            field: key,
            message: errors[key],
            trigger: 'submit',
          }
          this.$gtm.push(gtmEvent)
        })
      }

      const paymentValidationErrors = this.paymentValidationErrors

      if (Object.keys(paymentValidationErrors).length) {
        Object.keys(paymentValidationErrors).forEach((key) => {
          const gtmEvent = {
            event: 'booking_form_error',
            field: 'payment',
            message: paymentValidationErrors[key],
            trigger: 'submit',
          }
          this.$gtm.push(gtmEvent)
        })
      }
    } catch (e) {
      console.error(e)
    }

    if (document.querySelector('.error')) {
      this.$puScrollTo('.error', { duration: 300, offset: -50 })
    }
  }

  // immediate is needed for it to update the booking context with details
  // deep is needed as only some keys will be changed
  @Watch('userContactDetails', { immediate: true, deep: true })
  updateContactDetails(details: ContactDetails) {
    if (process.client) {
      let contactDetails = details
      if (this.context.contactDetails) {
        contactDetails = this.mergeContactDetails(
          this.context.contactDetails,
          details,
        )
      }
      this.send({ type: 'UPDATE_CONTACT_DETAILS', details: contactDetails })
      this.registerInterest()
    }
  }

  mergeContactDetails(
    base: ContactDetails,
    override: ContactDetails,
  ): ContactDetails {
    // TODO: could be more generic
    return {
      address: {
        city: override.address?.city || base.address?.city || '',
        country: override.address?.country || base.address?.country || '',
        postCode: override.address?.postCode || base.address?.postCode || '',
        street: override.address?.street || base.address?.street || '',
        noPostCode:
          override.address?.noPostCode || base.address?.noPostCode || '',
      },
      email: override.email || base.email || '',
      firstName: override.firstName || base.firstName || '',
      lastName: override.lastName || base.lastName || '',
      phone: {
        number: override.phone?.number || base.phone?.number || '',
        countryDialingCode:
          override.phone?.countryDialingCode ||
          base.phone?.countryDialingCode ||
          '',
        countryDialingNumber:
          override.phone?.countryDialingNumber ||
          base.phone?.countryDialingNumber ||
          '',
      },
      sendTextMessage:
        override.sendTextMessage || base.sendTextMessage || false,
      password: override.password || base.password || '',
    }
  }

  @Watch('userOtherDetails', { immediate: true, deep: true })
  async userOtherDetailsUpdated(details: OtherDetails) {
    if (process.client) {
      const otherDetails = { ...details }
      const unitTypeAvailable =
        details.unitType &&
        this.details.subcategoriesRequiringDimensions?.find(
          (subCat) => subCat.id === details.unitType,
        )

      if (details.unitType && !unitTypeAvailable) {
        otherDetails.unitType = ''
        otherDetails.unitWidth = ''
        otherDetails.unitDepth = ''
      }

      this.updateOtherDetails(otherDetails)

      if (
        unitTypeAvailable &&
        (otherDetails.unitWidth || otherDetails.unitDepth)
      ) {
        await this.$nextTick()
        this.$refs.bookingOtherDetails?.$refs.unitTypeObserver?.validate()
      }
    }
  }

  updateOtherDetails(details: OtherDetails) {
    this.send({ type: 'UPDATE_OTHER_DETAILS', details })
  }

  @Watch('paymentMethodDetails')
  clearPaymentErrorsOnPaymentMethodDetailsChanged(
    { saveCard: newSaveCard }: { saveCard: boolean },
    { saveCard: oldSaveCard }: { saveCard?: boolean },
  ) {
    const saveCardNotChanged =
      oldSaveCard !== undefined && newSaveCard === oldSaveCard
    if (saveCardNotChanged || newSaveCard === undefined) {
      this.clearPaymentErrors()
    }
  }

  clearPaymentErrors() {
    this.paymentValidationErrors = []
  }

  handlePaymentElementError(error: string) {
    this.paymentValidationErrors = [error]
    this.scrollToFirstError()
  }

  @Watch('paymentError')
  setPaymentError() {
    if (this.paymentError) {
      this.paymentValidationErrors.push(this.paymentError)
      this.$puScrollTo('.payment', { duration: 300, offset: -20 })
    }
  }

  get isTotalPriceCurrencyDifferentThanUsersCurrency() {
    return (
      this.quote.priceTotal.currency !== this.currencyStore.currentCurrencyCode
    )
  }

  get contactDetails() {
    return this.context.contactDetails || this.userContactDetails
  }

  get otherDetails() {
    return this.context.otherDetails || this.userOtherDetails
  }

  get paymentError(): string {
    return this.context?.paymentStatus?.result?.error || ''
  }

  sectionHeaderVisibilityChanged(
    isVisible: boolean,
    { target }: { target: HTMLElement },
  ) {
    if (isVisible) {
      target.classList.add('loaded')
    }
  }

  onStripeInit(stripe) {
    this.stripeInitialized = true
    this.log('stripe_initialized_in_form', {
      hasStripe: !!stripe,
      hasStripeToken: !!this.stripeStore.stripeToken,
      stripeAccount: this.stripeStore.stripeToken?.customer_id || 'none',
      paymentMethod: this.paymentMethodDetails.method,
    })
  }
}

export default toNative(BookingForm)
</script>

<style scoped>
section {
  padding: 1.4em;
}

section.payment {
  padding: 1.4em 1.2em;
}

section section {
  border-bottom: none;
  padding: 0;
}

section.checkout {
  padding: 0;
}

.section-header {
  position: relative;
}

.section-header > span {
  background-color: #0072bc;
  color: white;
  display: block;
  font-weight: bold;
  padding: 0.5em 1em 0.5em 1.4em;
  overflow-wrap: anywhere;
  hyphens: auto;
}

.section-header[data-booking-progress] > span {
  padding: 0.5em 1em 1em 1.4em;
}

.section-header .icon {
  width: 1.8em;
  height: auto;
  margin-right: 0.5em;
}

.section-header[data-booking-progress] > span::after,
.section-header[data-booking-progress]::after {
  content: '';
  display: inline-block;
  background: var(--colour-light-blue);
  height: 0.2em;
  position: absolute;
  bottom: 0.5em;
  right: 1em;
  left: 1.5em;
  border-radius: 0.1em;
}

.section-header[data-booking-progress] > span::after {
  background: white;
  width: calc(100% - 3em);
  border-radius: 0.1em;
}

[data-booking-progress]::after {
  width: 0;
  transition: width 3s ease-in-out;
}

[data-booking-progress='1'].loaded::after {
  width: 10%;
  transition-duration: 1s;
}

[data-booking-progress='4']::after {
  width: 10%;
}

[data-booking-progress='4'].loaded::after {
  width: 40%;
}

[data-booking-progress='6']::after {
  width: 40%;
}

[data-booking-progress='6'].loaded::after {
  width: 60%;
}

.section-header.secure-section > span::after,
.section-header.secure-section::after {
  left: 3em;
}

.section-header.secure-section > span::after {
  width: calc(100% - 4.5em);
}

.section-header.secure-section > span {
  padding-left: 3em;
}

.section-header:first-of-type,
.cancellation-policy,
.payment,
.please-note {
  background-color: #eff6ff;
}

.my-details-wrapper {
  min-height: 52em;
}

.usefullinfo {
  font-size: var(--body);
  margin: 0.6em 0;
}

.padlock {
  width: 3em;
  position: absolute;
  left: 0;
  top: auto;
}

.secure-note-popover-trigger {
  display: flex;
  align-items: center;
  color: white;
}

.secure-note-popover-trigger span {
  font-size: var(--body);
  color: #fff;
  text-align: left;
}

.secure-note-popover-trigger img {
  display: inline-block;
  width: 1.4em;
  height: 1.4em;
  margin-left: 0.35em;
  filter: grayscale(1) brightness(200%);
}

.secure-section :deep(.info-button img) {
  opacity: 1;
}

.submit-wrapper {
  padding: 1.4em;
}

.payment-request-button-container {
  margin-bottom: 1em;
}

.paypal-button {
  isolation: isolate;
  margin-bottom: 1em;
}

.submit-wrapper .help-text {
  margin-top: 1.5em;
  text-align: center;
}

.please-note section {
  border-bottom: 1px solid var(--colour-pale-blue);
  justify-content: space-between;
  margin: 0 0 0.7em 0;
  padding: 0 0 0.7em 0;
}

.please-note p {
  font-size: var(--body);
  margin: 0 0 0.5em 0;
}

.booking-page :deep(.help-text) {
  font-size: var(--small);
}

.currency-footnote {
  color: var(--colour-text-light);
  font-size: var(--small);
  margin-top: 0.5em;
  padding-top: 1em;
}

.accept-terms {
  font-size: var(--body);
  padding: 1.4em 1.4em 0;
}

.ts-and-cs-title {
  font-weight: normal;
  font-size: var(--body);
}

.ts-and-cs-content {
  font-size: var(--body);
  padding: 1.4em;
}

.ts-and-cs-content :deep(.translation-text) {
  margin-top: 1em;
}

.my-details-wrapper :deep(.password-section) {
  margin-bottom: -1.4em;
}

.campsite-notices,
.permitted-times {
  font-size: var(--body);
}

.permitted-times :deep(p) {
  margin: 0 0 0.25em 0;
}

.permitted-times :deep(table),
.permitted-times :deep(tr) {
  border: none;
}

.permitted-times :deep(td) {
  padding: 0;
}

.permitted-times :deep(td.title) {
  padding-right: 0.5em;
}

.guests-age-warning {
  font-size: var(--body);
  justify-content: flex-start;
  align-items: flex-start;
  color: #9e0000;
  display: flex;
  font-weight: bold;
  margin: 0;
  padding: 0.75em 1.4em 0;
}

.guests-age-warning img {
  width: 1.2em;
  height: auto;
  position: relative;
  margin: 0.1em 0.4em 0 0;
}

/* desktop & tablet */
.booking-page {
  --max-page-width: 698px;
}

.desktop .section-header {
  font-size: 1.5em;
}

.desktop .usefullinfo,
.desktop .please-note p,
.desktop .accept-terms,
.desktop .ts-and-cs-title,
.desktop .ts-and-cs-content,
.desktop .campsite-notices,
.desktop .permitted-times,
.desktop .guests-age-warning {
  font-size: revert;
}

.booking-page.desktop .currency-footnote {
  font-size: var(--body);
}

.desktop .section-header > span,
.desktop .section-header[data-booking-progress] > span {
  padding-left: calc((100% - var(--legacy-page-width-max)) / 2);
}

.desktop .section-header[data-booking-progress] > span::after {
  width: var(--legacy-page-width-max);
}

.desktop .section-header[data-booking-progress] > span::after,
.desktop .section-header[data-booking-progress]::after {
  left: calc((100% - var(--legacy-page-width-max)) / 2);
}

.desktop .section-header.secure-section > span {
  padding-left: calc((100% - var(--legacy-page-width-max) + 3em) / 2);
}

.desktop .section-header.secure-section[data-booking-progress] > span::after,
.desktop .section-header.secure-section[data-booking-progress]::after {
  left: calc((100% - var(--legacy-page-width-max) + 3em) / 2);
}

.desktop .section-header.secure-section[data-booking-progress] > span::after {
  width: calc(var(--legacy-page-width-max) - 1.5em);
}

.desktop .section-header.secure-section aside {
  display: block;
}

.desktop .padlock {
  left: calc((100% - var(--legacy-page-width-max) - 3em) / 2);
}

.desktop .my-details-wrapper :deep(.password-section) {
  margin: 2em 0 0;
  padding: 2em;
  border-radius: 0.6em;
}

.desktop .my-details-wrapper :deep(.password) {
  margin: 1em 0 0;
  padding: 1em 1.4em 1.4em;
  font-size: inherit;
  border-radius: 0.6em;
}

.desktop [data-booking-progress='1'].loaded::after,
.desktop [data-booking-progress='4']::after {
  width: calc((var(--max-page-width) / 100) * 10);
}

.desktop [data-booking-progress='6']::after,
.desktop [data-booking-progress='4'].loaded::after {
  width: calc((var(--max-page-width) / 100) * 40);
}

.desktop [data-booking-progress='6'].loaded::after {
  width: calc((var(--max-page-width) / 100) * 60);
}

.desktop :deep(.units) {
  margin-top: 0.2em;
}

/* small tablets */
@media (max-width: 768px) {
  .booking-page {
    --max-page-width: 94%;
  }

  .desktop .section-header > span,
  .desktop .section-header[data-booking-progress] > span {
    padding-left: 1.4em;
  }

  .desktop .section-header[data-booking-progress] > span::after {
    width: var(--max-page-width);
  }

  .desktop .section-header[data-booking-progress] > span::after,
  .desktop .section-header[data-booking-progress]::after {
    left: calc((100% - var(--max-page-width)) / 2);
  }

  .desktop .section-header.secure-section > span {
    padding-left: calc((100% - var(--max-page-width) + 3em) / 2);
  }

  .desktop .section-header.secure-section[data-booking-progress] > span::after,
  .desktop .section-header.secure-section[data-booking-progress]::after {
    left: calc((100% - var(--max-page-width) + 3em) / 2);
  }

  .desktop .section-header.secure-section[data-booking-progress] > span::after {
    width: calc(var(--max-page-width) - 1.5em);
  }
}
</style>
